/**
 * CoachDIA Discovery — Mode RH / B2B : aperçu commercial.
 *
 * ⚠️ Marque user-facing : « COACHDIA · PRISM ». URLs /discovery-demo, data-testid et
 * nom du fichier DiscoveryDemo.jsx volontairement conservés pour préserver SEO / QA.
 *
 * Permet à un acheteur RH / L&D de prévisualiser intégralement l'expérience
 * Discovery (4 archétypes de démo, profil complet, PDF avec filigrane) avant
 * décision d'achat de licences pour son équipe.
 *
 * Public, pas d'auth requise. Filigrane « APERÇU B2B » visible.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Download, ArrowRight, Users, Sparkles, Shield, Loader2, CheckCircle2,
  Target, AlertCircle, Compass, X,
} from 'lucide-react';
import { getApiUrl } from '../config/environment';

const API = getApiUrl();

const ENERGY_COLORS = {
  red: { hex: '#C0392B', label: 'ROUGE · Action' },
  yellow: { hex: '#D4A017', label: 'JAUNE · Inspiration' },
  green: { hex: '#2D8659', label: 'VERT · Harmonie' },
  blue: { hex: '#1E4F8C', label: 'BLEU · Analyse' },
};

// ───── Radar mini ─────
const MiniRadar = ({ colors = {} }) => {
  const cx = 120, cy = 120, r = 80;
  const axes = [
    { key: 'red', dx: 0, dy: -1 },
    { key: 'yellow', dx: 1, dy: 0 },
    { key: 'green', dx: 0, dy: 1 },
    { key: 'blue', dx: -1, dy: 0 },
  ];
  const pts = axes.map(a =>
    `${cx + a.dx * r * ((colors[a.key] || 0) / 100)},${cy + a.dy * r * ((colors[a.key] || 0) / 100)}`
  ).join(' ');
  return (
    <svg viewBox="0 0 240 240" className="w-full h-auto">
      {[25, 50, 75, 100].map((p, i) => (
        <circle key={i} cx={cx} cy={cy} r={r * p / 100} fill="none" stroke="#e5d9bc" strokeWidth="1" />
      ))}
      {axes.map((a, i) => (
        <line key={i} x1={cx} y1={cy} x2={cx + a.dx * r} y2={cy + a.dy * r} stroke="#e5d9bc" strokeWidth="1" />
      ))}
      <polygon points={pts} fill="#92710A" fillOpacity="0.25" stroke="#92710A" strokeWidth="2" />
      {axes.map((a, i) => (
        <circle key={i} cx={cx + a.dx * r * ((colors[a.key] || 0) / 100)}
                cy={cy + a.dy * r * ((colors[a.key] || 0) / 100)}
                r="5" fill={ENERGY_COLORS[a.key].hex} />
      ))}
    </svg>
  );
};

const ArchetypeButton = ({ archetype, active, onClick }) => (
  <button
    data-testid={`demo-archetype-${archetype.color}`}
    onClick={onClick}
    className={`text-left p-4 border-2 rounded-sm transition-all ${
      active
        ? 'border-[#92710A] bg-[#FBF9F5] shadow-md'
        : 'border-stone-200 hover:border-[#92710A]/60 bg-white'
    }`}
  >
    <div className="flex items-center gap-2 mb-1">
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ENERGY_COLORS[archetype.color].hex }} />
      <span className="text-xs tracking-widest uppercase text-stone-500">{ENERGY_COLORS[archetype.color].label.split(' · ')[0]}</span>
    </div>
    <div className="font-serif text-lg text-stone-900">{archetype.archetype_name}</div>
    <div className="text-xs italic text-stone-600 mt-1">« {archetype.archetype_tagline} »</div>
    <div className="text-[10px] tracking-widest text-[#92710A] mt-2">{archetype.mbti_code}</div>
  </button>
);

export default function DiscoveryDemo() {
  const [archetypes, setArchetypes] = useState([]);
  const [selected, setSelected] = useState('red');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(() => {
    try { return localStorage.getItem('discovery_b2b_lead_submitted') === '1'; }
    catch { return false; }
  });
  const [leadForm, setLeadForm] = useState({ email: '', company: '', role: '', team_size: '' });
  const [leadSending, setLeadSending] = useState(false);

  // Initial load
  useEffect(() => {
    axios.get(`${API}/discovery/demo/archetypes`)
      .then(r => setArchetypes(r.data.archetypes || []))
      .catch(() => toast.error('Impossible de charger les archétypes démo'));
  }, []);

  const loadProfile = useCallback(async (color) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/discovery/demo/profile`, { params: { archetype: color } });
      setProfile(r.data);
    } catch (e) {
      toast.error('Erreur de chargement du profil démo');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProfile(selected); }, [selected, loadProfile]);

  const triggerPdfDownload = async () => {
    setDownloadingPdf(true);
    try {
      const r = await axios.get(`${API}/discovery/demo/pdf`, {
        params: { archetype: selected },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `coachdia-prism-demo-${selected}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Aperçu PDF téléchargé');
    } catch {
      toast.error('Erreur de téléchargement');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadPdf = () => {
    // Soft CRM form avant le 1er download (skip si déjà soumis sur ce navigateur)
    if (!leadSubmitted) {
      setLeadModalOpen(true);
      return;
    }
    triggerPdfDownload();
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!leadForm.email.includes('@')) {
      toast.error('Merci de renseigner un email valide');
      return;
    }
    setLeadSending(true);
    try {
      await axios.post(`${API}/discovery/demo/lead`, {
        ...leadForm,
        archetype_viewed: selected,
      });
      try { localStorage.setItem('discovery_b2b_lead_submitted', '1'); } catch {}
      setLeadSubmitted(true);
      setLeadModalOpen(false);
      toast.success('Merci ! Téléchargement en cours...');
      triggerPdfDownload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Soumission échouée');
    } finally {
      setLeadSending(false);
    }
  };

  const skipLead = () => {
    // Non bloquant : on autorise le skip pour ne pas frictionner l'UX
    try { localStorage.setItem('discovery_b2b_lead_submitted', '1'); } catch {}
    setLeadSubmitted(true);
    setLeadModalOpen(false);
    triggerPdfDownload();
  };

  const full = profile?.full;
  const narr = full?.narrative || {};
  const scores = full?.scores || {};
  const archetype = full?.archetype || {};

  return (
    <div className="min-h-screen bg-stone-50" data-testid="discovery-demo-page">
      {leadModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
             data-testid="demo-lead-modal"
             onClick={skipLead}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitLead}
                className="bg-white max-w-md w-full p-6 rounded-sm shadow-2xl relative">
            <button type="button" onClick={skipLead} data-testid="demo-lead-skip"
                    className="absolute top-3 right-3 text-stone-400 hover:text-stone-600">
              <X className="w-4 h-4" />
            </button>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] mb-2">Avant de télécharger</div>
            <h3 className="font-serif text-2xl text-stone-900 mb-1">Quelques informations — optionnelles</h3>
            <p className="text-xs text-stone-500 mb-5">Elles nous permettent de vous recontacter avec des ressources ciblées. Vous pouvez passer cette étape.</p>
            <div className="space-y-3">
              <input
                type="email"
                required
                placeholder="Email professionnel *"
                data-testid="demo-lead-email"
                value={leadForm.email}
                onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-sm text-sm focus:border-[#92710A] focus:outline-none"
              />
              <input
                type="text"
                placeholder="Entreprise"
                data-testid="demo-lead-company"
                value={leadForm.company}
                onChange={(e) => setLeadForm({ ...leadForm, company: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-sm text-sm focus:border-[#92710A] focus:outline-none"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="Rôle (RH, L&D...)"
                  data-testid="demo-lead-role"
                  value={leadForm.role}
                  onChange={(e) => setLeadForm({ ...leadForm, role: e.target.value })}
                  className="px-3 py-2 border border-stone-300 rounded-sm text-sm focus:border-[#92710A] focus:outline-none"
                />
                <select
                  data-testid="demo-lead-team-size"
                  value={leadForm.team_size}
                  onChange={(e) => setLeadForm({ ...leadForm, team_size: e.target.value })}
                  className="px-3 py-2 border border-stone-300 rounded-sm text-sm focus:border-[#92710A] focus:outline-none bg-white"
                >
                  <option value="">Taille équipe</option>
                  <option value="1-5">1–5</option>
                  <option value="6-20">6–20</option>
                  <option value="21-50">21–50</option>
                  <option value="51-200">51–200</option>
                  <option value="200+">200+</option>
                </select>
              </div>
            </div>
            <div className="mt-5 flex justify-between items-center gap-2">
              <button type="button" onClick={skipLead}
                      className="text-xs text-stone-500 hover:text-stone-700 underline">
                Télécharger sans renseigner
              </button>
              <Button type="submit" disabled={leadSending} data-testid="demo-lead-submit"
                      className="bg-[#0F0F10] text-white rounded-sm hover:bg-stone-800">
                {leadSending
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi...</>
                  : <>Continuer <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Bandeau B2B — toujours visible */}
      <div className="bg-[#0F0F10] text-white py-3" data-testid="demo-banner">
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-4 h-4 text-[#92710A]" />
            <span className="text-xs tracking-[0.2em] uppercase text-[#C4B27A]">APERÇU B2B · Usage commercial RH / L&D</span>
          </div>
          <a href="mailto:contact@coachdia.com?subject=Démo B2B COACHDIA Prism"
             data-testid="demo-cta-contact"
             className="text-xs tracking-widest uppercase text-[#92710A] hover:underline">
            Planifier un échange commercial →
          </a>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-10">
          <div className="text-xs tracking-[0.3em] uppercase text-[#92710A] mb-3">COACHDIA · PRISM · Mode Démonstration</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-stone-900 leading-tight max-w-3xl">
            Voyez ce que vos collaborateurs vont recevoir — sans passer le test.
          </h1>
          <p className="mt-4 text-stone-600 max-w-2xl">
            Quatre archétypes représentatifs. Pour chacun : le radar d'énergies, l'archétype complet,
            les forces, les angles morts, les scénarios réels, les recommandations signatures et les
            projections à 12 mois. Exactement ce que vos équipes recevront.
          </p>
        </div>

        {/* Switcher archétypes */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10" data-testid="demo-archetype-switcher">
          {archetypes.map(a => (
            <ArchetypeButton
              key={a.color}
              archetype={a}
              active={selected === a.color}
              onClick={() => setSelected(a.color)}
            />
          ))}
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-stone-500 py-10">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement du profil...
          </div>
        )}

        {!loading && profile && (
          <div className="grid lg:grid-cols-3 gap-8" data-testid="demo-profile-content">
            {/* Sidebar — radar + archetype */}
            <aside className="lg:col-span-1 space-y-6">
              <Card className="p-6 border-stone-200">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] mb-2">Archétype</div>
                <div className="font-serif text-2xl text-stone-900">{archetype.name}</div>
                <div className="italic text-stone-500 text-sm mt-1">« {archetype.tagline} »</div>
                <div className="mt-4 text-sm text-stone-700 leading-relaxed">{archetype.description}</div>
              </Card>

              <Card className="p-6 border-stone-200">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] mb-4">Cartographie d'énergies</div>
                <MiniRadar colors={scores.colors} />
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {['red', 'yellow', 'green', 'blue'].map(c => (
                    <div key={c} className="flex items-center justify-between text-xs">
                      <span className="text-stone-600">{ENERGY_COLORS[c].label.split(' · ')[1]}</span>
                      <span className="font-semibold" style={{ color: ENERGY_COLORS[c].hex }}>
                        {Math.round(scores.colors?.[c] || 0)}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>

              <Button
                onClick={downloadPdf}
                disabled={downloadingPdf}
                data-testid="demo-download-pdf"
                className="w-full bg-[#0F0F10] hover:bg-stone-800 text-white rounded-sm"
              >
                {downloadingPdf
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération...</>
                  : <><Download className="w-4 h-4 mr-2" /> Télécharger l'aperçu PDF</>}
              </Button>
            </aside>

            {/* Main column — narrative complet */}
            <main className="lg:col-span-2 space-y-10">
              {/* Synthèse exec */}
              <section data-testid="demo-exec-summary">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] mb-2">Synthèse exécutive</div>
                <p className="font-serif text-lg text-stone-800 leading-relaxed">
                  {narr.executive_summary}
                </p>
              </section>

              {/* Forces */}
              <section data-testid="demo-strengths">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-4 h-4 text-[#92710A]" />
                  <h2 className="font-serif text-2xl text-stone-900">Vos forces majeures</h2>
                </div>
                <div className="space-y-3">
                  {(narr.strengths || []).map((s, i) => (
                    <div key={i} className="border-l-4 border-[#92710A] bg-[#FBF9F5] p-4 rounded-r-sm">
                      <div className="font-semibold text-stone-900">{s.title}</div>
                      <p className="text-sm text-stone-700 mt-1 leading-relaxed">{s.detail}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Angles morts enrichis */}
              <section data-testid="demo-blind-spots">
                <div className="flex items-center gap-2 mb-4">
                  <AlertCircle className="w-4 h-4 text-[#C0392B]" />
                  <h2 className="font-serif text-2xl text-stone-900">Angles morts</h2>
                </div>
                <div className="space-y-4">
                  {(narr.blind_spots || []).map((b, i) => (
                    <div key={i} className="border-l-4 border-[#C0392B] bg-white p-4 rounded-r-sm shadow-sm">
                      <div className="font-semibold text-stone-900">{b.title}</div>
                      <p className="text-xs italic text-stone-500 mt-1">Déclencheur : {b.trigger}</p>
                      <p className="text-sm text-stone-700 mt-2 leading-relaxed">{b.detail}</p>
                      {b.situation && (
                        <div className="mt-3 grid gap-1.5 text-xs">
                          <div><span className="text-[#92710A] font-semibold tracking-wider uppercase text-[10px] mr-2">Situation</span>{b.situation}</div>
                          <div><span className="text-[#92710A] font-semibold tracking-wider uppercase text-[10px] mr-2">Ce qu'on voit</span>{b.observable_behavior}</div>
                          <div><span className="text-[#92710A] font-semibold tracking-wider uppercase text-[10px] mr-2">Impact réel</span>{b.impact}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Scénarios */}
              {narr.scenarios && narr.scenarios.length > 0 && (
                <section data-testid="demo-scenarios">
                  <div className="flex items-center gap-2 mb-4">
                    <Compass className="w-4 h-4 text-[#92710A]" />
                    <h2 className="font-serif text-2xl text-stone-900">Vous en situation</h2>
                  </div>
                  <div className="space-y-4">
                    {narr.scenarios.map((s, i) => (
                      <div key={i} className="border-t border-stone-200 pt-4">
                        <div className="text-xs tracking-widest uppercase text-[#92710A] font-semibold">{s.context}</div>
                        <p className="text-sm text-stone-700 mt-2"><span className="text-[10px] tracking-wider uppercase text-stone-400 mr-2">Ce qui se passe</span>{s.likely_behavior}</p>
                        <p className="text-sm text-stone-900 mt-2 font-medium"><span className="text-[10px] tracking-wider uppercase text-stone-400 mr-2">Mouvement à activer</span>{s.recommended_move}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Recommandations signatures */}
              {narr.signature_recommendations && narr.signature_recommendations.length > 0 && (
                <section data-testid="demo-signature-recos">
                  <div className="flex items-center gap-2 mb-4">
                    <Target className="w-4 h-4 text-[#92710A]" />
                    <h2 className="font-serif text-2xl text-stone-900">Recommandations signatures</h2>
                  </div>
                  <div className="space-y-4">
                    {narr.signature_recommendations.map((r, i) => (
                      <div key={i} className="border border-stone-200 border-l-4 border-l-[#92710A] p-4 rounded-r-sm">
                        <div className="font-serif text-lg text-stone-900 mb-2">{r.title}</div>
                        <div className="space-y-1.5 text-sm">
                          <div><span className="text-[10px] tracking-widest uppercase text-[#92710A] font-semibold mr-2">Contexte</span>{r.context}</div>
                          <div><span className="text-[10px] tracking-widest uppercase text-[#92710A] font-semibold mr-2">Action</span>{r.action}</div>
                          <div><span className="text-[10px] tracking-widest uppercase text-[#92710A] font-semibold mr-2">Bénéfice</span>{r.benefit}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Projections 12 mois */}
              {narr.trajectories && (
                <section data-testid="demo-trajectories">
                  <h2 className="font-serif text-2xl text-stone-900 mb-4">Projections à 12 mois</h2>
                  <div className="grid gap-4">
                    <div className="p-5 bg-[#FBF9F5] border border-stone-200 border-l-4 border-l-amber-800 rounded-r-sm">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-stone-600 font-semibold mb-2">Si rien ne change</div>
                      <p className="text-sm text-stone-800 leading-relaxed">{narr.trajectories.if_static}</p>
                    </div>
                    <div className="p-5 bg-[#0F0F10] text-white border-l-4 border-[#92710A] rounded-r-sm">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[#C4B27A] font-semibold mb-2">Si le travail est engagé</div>
                      <p className="text-sm leading-relaxed">{narr.trajectories.if_engaged}</p>
                    </div>
                  </div>
                </section>
              )}

              {/* CTA conversion B2B */}
              <section className="bg-[#0F0F10] text-white p-8 rounded-sm mt-12">
                <div className="flex items-center gap-2 text-[#92710A] text-xs tracking-[0.3em] uppercase mb-3">
                  <Users className="w-4 h-4" /> Pour vos équipes
                </div>
                <h3 className="font-serif text-2xl mb-3">Déployez COACHDIA Prism dans votre organisation</h3>
                <ul className="space-y-2 text-sm text-stone-300 mb-6">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#92710A] shrink-0 mt-0.5" /> Profils individuels pour chaque collaborateur</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#92710A] shrink-0 mt-0.5" /> Cartographie d'équipe (en option)</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#92710A] shrink-0 mt-0.5" /> Coachs humains certifiés, recommandés par archétype</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#92710A] shrink-0 mt-0.5" /> Facturation adaptée — devis sur mesure</li>
                </ul>
                <a href="mailto:contact@coachdia.com?subject=Déploiement COACHDIA Prism B2B"
                   data-testid="demo-cta-enterprise">
                  <Button className="bg-[#92710A] hover:bg-[#7a5d08] text-white rounded-sm">
                    Discuter d'un déploiement <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </section>
            </main>
          </div>
        )}
      </div>
    </div>
  );
}
